import { useEffect, useMemo, useState } from "react";
import { TaskDisclosure } from "../components/TaskDisclosure";

const ARMORY_SECTION_IDS = ["overview", "issue", "service", "inventory"];

const EMPTY_FIREARM_FORM = {
  make: "",
  model: "",
  caliber: "",
  serialNumber: "",
  assetTag: "",
  storageLocation: "",
  roundCount: "0",
  serviceTrigger: "date_or_rounds",
  maintenanceDueAt: "",
  maintenanceIntervalDays: "",
  maintenanceRoundInterval: "",
  maintenancePlanName: "Firearm inspection",
  maintenanceChecklist: "Clean and inspect bore\nInspect optic and mounts\nFunction check action and safety",
  notes: ""
};

function normalizeArmorySection(value) {
  return ARMORY_SECTION_IDS.includes(value) ? value : "overview";
}

function statusLabel(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function checklistItemsFromText(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function checklistItemsToText(items = []) {
  return items
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join("\n");
}

function dateOnly(value) {
  if (!value) {
    return "Not scheduled";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function isoDateOnly(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

function parseDate(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parsePositiveInt(value) {
  const parsed = Number.parseInt(value || "0", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function addDaysIso(days = 0, from = new Date()) {
  const date = from instanceof Date ? new Date(from) : new Date(from);
  const safeDays = Number.parseInt(days || 0, 10);
  if (!Number.isFinite(safeDays) || safeDays <= 0 || Number.isNaN(date.getTime())) {
    return "";
  }
  date.setDate(date.getDate() + safeDays);
  return isoDateOnly(date);
}

function firearmName(resource) {
  const make = resource.attributes?.firearmMake || "";
  const model = resource.attributes?.firearmModel || "";
  const composed = `${make} ${model}`.trim();
  return composed || resource.name || "Armory item";
}

function firearmRecordNameFromDraft(draft) {
  return `${draft.make} ${draft.model}`.trim() || draft.assetTag || draft.serialNumber || "Armory firearm";
}

function serviceTriggerFromAttributes(attributes = {}) {
  const hasRoundRule = parsePositiveInt(attributes.maintenanceRoundInterval) > 0;
  const hasDateRule = Boolean(attributes.maintenanceDueAt) || parsePositiveInt(attributes.maintenanceIntervalDays) > 0;
  return attributes.maintenanceServiceTrigger ||
    (hasRoundRule && hasDateRule
      ? "date_or_rounds"
      : hasRoundRule
        ? "rounds"
        : hasDateRule
          ? "date"
          : "manual");
}

function firearmDraftFromResource(resource = {}) {
  const attributes = resource.attributes || {};
  return {
    make: attributes.firearmMake || "",
    model: attributes.firearmModel || "",
    caliber: attributes.caliber || "",
    serialNumber: attributes.serialNumber || "",
    assetTag: attributes.assetTag || "",
    storageLocation: attributes.storageLocation || "",
    roundCount: String(attributes.roundCount || "0"),
    serviceTrigger: serviceTriggerFromAttributes(attributes),
    maintenanceDueAt: attributes.maintenanceDueAt || "",
    maintenanceIntervalDays: String(attributes.maintenanceIntervalDays || ""),
    maintenanceRoundInterval: String(attributes.maintenanceRoundInterval || ""),
    maintenancePlanName: attributes.maintenancePlanName || "Firearm inspection",
    maintenanceChecklist: attributes.maintenanceChecklist || "",
    notes: attributes.notes || ""
  };
}

function eventLabel(event = {}) {
  return event.title || event.name || event.eventId || "Event";
}

function reservationLabel(reservation = {}) {
  const start = reservation.startsAt ? dateOnly(reservation.startsAt) : "";
  return [
    reservation.memberName || reservation.member?.fullName || reservation.contactName,
    reservation.resourceName,
    start
  ].filter(Boolean).join(" / ") || reservation.reservationId || "Reservation";
}

function checkoutAssignmentLabel(resource = {}) {
  const attributes = resource.attributes || {};
  const type = attributes.checkedOutAssignmentType || (attributes.checkedOutMemberId ? "member" : "");
  if (type === "staff") {
    return attributes.checkedOutStaffName || "Staff controlled";
  }
  if (type === "event") {
    return attributes.checkedOutEventName || "Event checkout";
  }
  if (type === "reservation") {
    return attributes.checkedOutReservationLabel || "Reservation checkout";
  }
  return attributes.checkedOutMemberName || "Member not recorded";
}

function isArmoryResource(resource) {
  return String(resource.attributes?.assetClass || "").toLowerCase() === "firearm" ||
    Boolean(resource.attributes?.firearmMake || resource.attributes?.firearmModel || resource.attributes?.caliber);
}

function maintenanceSignal(resource) {
  const attributes = resource.attributes || {};
  const serviceTrigger = serviceTriggerFromAttributes(attributes);
  const usesDate = serviceTrigger === "date" || serviceTrigger === "date_or_rounds";
  const usesRounds = serviceTrigger === "rounds" || serviceTrigger === "date_or_rounds";
  const dueAt = usesDate ? parseDate(attributes.maintenanceDueAt) : null;
  const roundInterval = usesRounds ? parsePositiveInt(attributes.maintenanceRoundInterval) : 0;
  const currentRounds = parsePositiveInt(attributes.roundCount);
  const lastServiceRounds = parsePositiveInt(attributes.maintenanceLastServiceRoundCount);

  let dateSignal = null;
  if (dueAt) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.ceil((dueAt.getTime() - today.getTime()) / 86400000);
    if (daysUntilDue < 0) {
      dateSignal = { label: `${Math.abs(daysUntilDue)}d overdue`, tone: "danger" };
    } else if (daysUntilDue === 0) {
      dateSignal = { label: "Due today", tone: "warning" };
    } else if (daysUntilDue <= 14) {
      dateSignal = { label: `Due in ${daysUntilDue}d`, tone: "warning" };
    } else {
      dateSignal = { label: dateOnly(attributes.maintenanceDueAt), tone: "ok" };
    }
  }

  let roundSignal = null;
  if (roundInterval > 0) {
    const roundsSinceService = Math.max(0, currentRounds - lastServiceRounds);
    const roundsRemaining = roundInterval - roundsSinceService;
    const warningWindow = Math.max(50, Math.ceil(roundInterval * 0.12));
    if (roundsRemaining < 0) {
      roundSignal = { label: `${Math.abs(roundsRemaining).toLocaleString()} rounds overdue`, tone: "danger" };
    } else if (roundsRemaining === 0) {
      roundSignal = { label: "Round service due", tone: "warning" };
    } else if (roundsRemaining <= warningWindow) {
      roundSignal = { label: `${roundsRemaining.toLocaleString()} rounds left`, tone: "warning" };
    } else {
      roundSignal = { label: `${roundsRemaining.toLocaleString()} rounds left`, tone: "ok" };
    }
  }

  const signals = [roundSignal, dateSignal].filter(Boolean);
  const danger = signals.find((signal) => signal.tone === "danger");
  if (danger) {
    return danger;
  }
  const warning = signals.find((signal) => signal.tone === "warning");
  if (warning) {
    return warning;
  }
  return signals[0] || { label: "No service rule", tone: "muted" };
}

function rentalStatusClass(value) {
  return String(value || "available").replace(/_/g, "-");
}

function readinessPercent(count, total) {
  if (!total) {
    return 0;
  }
  return Math.max(0, Math.round((count / total) * 100));
}

function recordHealthIssues(resource = {}) {
  const attributes = resource.attributes || {};
  const issues = [];
  if (!attributes.serialNumber) {
    issues.push("Serial missing");
  }
  if (!attributes.storageLocation) {
    issues.push("Storage missing");
  }
  if (serviceTriggerFromAttributes(attributes) === "manual") {
    issues.push("Service rule missing");
  }
  if (checklistItemsFromText(attributes.maintenanceChecklist).length === 0) {
    issues.push("Checklist missing");
  }
  if (attributes.roundCount === undefined || attributes.roundCount === null || attributes.roundCount === "") {
    issues.push("Round count missing");
  }
  return issues;
}

function reliabilityTextFromResource(resource = {}) {
  const attributes = resource.attributes || {};
  return [
    attributes.lastMalfunctionNotes,
    attributes.maintenanceNotes,
    attributes.notes,
    ...(attributes.maintenanceEvents || []).flatMap((event) => [event.title, event.note, event.action])
  ].filter(Boolean).join(" ").toLowerCase();
}

function hasReliabilityConcern(resource = {}) {
  return /\b(malfunction|failure|fail|jam|feed|extract|optic|damage|crack|unsafe|zero|repair|replace|broken)\b/.test(
    reliabilityTextFromResource(resource)
  );
}

function latestArmoryEvent(resource = {}) {
  const events = resource.attributes?.maintenanceEvents || [];
  return events[0] || null;
}

function serviceRuleLabel(attributes = {}) {
  const trigger = serviceTriggerFromAttributes(attributes);
  if (trigger === "date_or_rounds") {
    return "Date or rounds";
  }
  if (trigger === "rounds") {
    return "Round count";
  }
  if (trigger === "date") {
    return "Calendar";
  }
  return "Manual pull";
}

function servicePlanSnapshot(resource = {}) {
  const attributes = resource.attributes || {};
  const trigger = serviceTriggerFromAttributes(attributes);
  const usesDate = trigger === "date" || trigger === "date_or_rounds";
  const usesRounds = trigger === "rounds" || trigger === "date_or_rounds";
  const roundInterval = usesRounds ? parsePositiveInt(attributes.maintenanceRoundInterval) : 0;
  const currentRounds = parsePositiveInt(attributes.roundCount);
  const lastServiceRounds = parsePositiveInt(attributes.maintenanceLastServiceRoundCount);
  const roundsSinceService = Math.max(0, currentRounds - lastServiceRounds);
  const roundsRemaining = roundInterval > 0 ? roundInterval - roundsSinceService : null;
  return {
    checklistCount: checklistItemsFromText(attributes.maintenanceChecklist).length,
    currentRounds,
    dueAt: usesDate ? attributes.maintenanceDueAt : "",
    roundInterval,
    roundsRemaining,
    roundsSinceService,
    ruleLabel: serviceRuleLabel(attributes),
    signal: maintenanceSignal(resource),
    trigger
  };
}

function serviceEventLabel(event = {}) {
  const action = String(event.action || "").replace(/_/g, " ");
  return event.title || statusLabel(action || "Service action");
}

function eventTimestamp(event = {}) {
  const date = new Date(event.createdAt || event.completedAt || event.updatedAt || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function buildFirearmPayload(form) {
  const name = firearmRecordNameFromDraft(form);
  return {
    type: "area",
    name,
    status: "open",
    reservable: false,
    capacity: { shooters: 0, lanes: 0 },
    distance: { unit: "yards", min: 0, max: 0, fixed: [] },
    attributes: {
      environment: "indoor",
      assetClass: "firearm",
      firearmMake: form.make,
      firearmModel: form.model,
      caliber: form.caliber,
      serialNumber: form.serialNumber,
      assetTag: form.assetTag,
      storageLocation: form.storageLocation,
      roundCount: form.roundCount,
      rentalStatus: "available",
      maintenanceServiceTrigger: form.serviceTrigger,
      maintenanceDueAt: form.maintenanceDueAt,
      maintenancePlanName: form.maintenancePlanName,
      maintenanceIntervalDays: form.maintenanceIntervalDays,
      maintenanceRoundInterval: form.maintenanceRoundInterval,
      maintenanceLastServiceRoundCount: form.roundCount,
      maintenanceChecklist: form.maintenanceChecklist,
      notes: form.notes
    }
  };
}

export function ArmoryPage({
  loading,
  events = [],
  members = [],
  onCreateArmoryItem,
  onUpdateResource,
  reservations = [],
  resources = [],
  savingArmoryItem,
  updatingResourceId
}) {
  const [firearmForm, setFirearmForm] = useState(EMPTY_FIREARM_FORM);
  const [armoryView, setArmoryView] = useState("service");
  const [armoryFilters, setArmoryFilters] = useState({ q: "" });
  const [returnDrafts, setReturnDrafts] = useState({});
  const [checkoutDrafts, setCheckoutDrafts] = useState({});
  const [maintenanceDrafts, setMaintenanceDrafts] = useState({});
  const [editDrafts, setEditDrafts] = useState({});
  const [addFirearmFormKey, setAddFirearmFormKey] = useState(0);
  const [activeArmorySection, setActiveArmorySection] = useState(() => {
    if (typeof window === "undefined") {
      return "overview";
    }
    return normalizeArmorySection(window.location.hash.replace("#armory-", ""));
  });

  const firearms = useMemo(() => resources.filter(isArmoryResource), [resources]);
  const checkedOutFirearms = useMemo(() => firearms.filter((item) => item.attributes?.rentalStatus === "checked_out"), [firearms]);
  const maintenanceFirearms = useMemo(() => firearms.filter((item) => (
    item.status === "maintenance" || item.attributes?.rentalStatus === "maintenance"
  )), [firearms]);
  const retiredFirearms = useMemo(() => firearms.filter((item) => item.attributes?.rentalStatus === "retired"), [firearms]);
  const dueSoonFirearms = useMemo(() => firearms.filter((item) => {
    const signal = maintenanceSignal(item);
    return signal.tone === "warning" || signal.tone === "danger";
  }), [firearms]);
  const issueReadyFirearms = useMemo(() => firearms.filter((item) => {
    const rentalStatus = item.attributes?.rentalStatus || "available";
    const signal = maintenanceSignal(item);
    return rentalStatus === "available" && signal.tone !== "warning" && signal.tone !== "danger";
  }), [firearms]);
  const attentionCount = maintenanceFirearms.length + dueSoonFirearms.filter((item) => item.attributes?.rentalStatus !== "maintenance").length;
  const armoryViewOptions = [
    { key: "service", label: "Service watch", count: attentionCount },
    { key: "ready", label: "Ready to issue", count: issueReadyFirearms.length },
    { key: "issued", label: "Checked out", count: checkedOutFirearms.length }
  ];
  const armorySections = [
    { id: "overview", label: "Overview", meta: attentionCount > 0 ? `${attentionCount} watch` : "Clear" },
    { id: "issue", label: "Issue desk", meta: `${issueReadyFirearms.length} ready` },
    { id: "service", label: "Service", meta: `${dueSoonFirearms.length} due` },
    { id: "inventory", label: "Inventory", meta: `${firearms.length} records` }
  ];
  const commandSummary = firearms.length === 0
    ? "No firearm records are ready for checkout yet."
    : attentionCount > 0
      ? `${attentionCount} firearm${attentionCount === 1 ? " needs" : "s need"} service attention.`
      : `${issueReadyFirearms.length} firearm${issueReadyFirearms.length === 1 ? "" : "s"} ready to issue.`;
  const activeSectionCopy = {
    overview: {
      kicker: "Armory intelligence",
      title: "Readiness before issue",
      description: "Start with the risk picture: what can go out, what needs to be held, and which records need attention."
    },
    issue: {
      kicker: "Issue desk",
      title: "Checkout and return work",
      description: "Work the daily queue without scanning every insight panel. Use the ledger filters for service watch, ready firearms, and checked-out firearms."
    },
    service: {
      kicker: "Service review",
      title: "Rounds, reliability, and service history",
      description: "Review maintenance rules, round pressure, reliability notes, and service history separately from the counter workflow."
    },
    inventory: {
      kicker: "Inventory records",
      title: "Create and maintain firearm records",
      description: "Add firearms and clean up identity, custody, service rules, checklist steps, and staff notes."
    }
  }[activeArmorySection];
  const armoryInsights = useMemo(() => {
    const serviceWatch = firearms
      .filter((item) => {
        const rentalStatus = item.attributes?.rentalStatus || "available";
        const signal = maintenanceSignal(item);
        return rentalStatus === "maintenance" || signal.tone === "warning" || signal.tone === "danger";
      })
      .sort((first, second) => {
        const firstSignal = maintenanceSignal(first);
        const secondSignal = maintenanceSignal(second);
        const toneScore = { danger: 0, warning: 1, ok: 2, muted: 3 };
        return (toneScore[firstSignal.tone] ?? 3) - (toneScore[secondSignal.tone] ?? 3);
      });
    const recordHealthItems = firearms
      .map((resource) => ({ resource, issues: recordHealthIssues(resource) }))
      .filter((item) => item.issues.length > 0);
    const reliabilityItems = firearms
      .filter(hasReliabilityConcern)
      .map((resource) => ({
        resource,
        event: latestArmoryEvent(resource),
        note: resource.attributes?.lastMalfunctionNotes || resource.attributes?.maintenanceNotes || latestArmoryEvent(resource)?.note || resource.attributes?.notes || "Review recent notes before issue."
      }))
      .slice(0, 4);
    const totalRounds = firearms.reduce((total, item) => total + parsePositiveInt(item.attributes?.roundCount), 0);
    const roundRuleCount = firearms.filter((item) => {
      const trigger = serviceTriggerFromAttributes(item.attributes || {});
      return trigger === "rounds" || trigger === "date_or_rounds";
    }).length;
    return {
      recordHealthItems,
      reliabilityItems,
      roundRuleCount,
      serviceWatch,
      totalRounds
    };
  }, [firearms]);
  const readinessMix = [
    { key: "ready", label: "Ready", count: issueReadyFirearms.length },
    { key: "issued", label: "Issued", count: checkedOutFirearms.length },
    { key: "watch", label: "Service watch", count: attentionCount },
    { key: "retired", label: "Retired", count: retiredFirearms.length }
  ];
  const averageRounds = firearms.length ? Math.round(armoryInsights.totalRounds / firearms.length) : 0;
  const servicePlanItems = useMemo(() => firearms
    .map((resource) => ({ resource, plan: servicePlanSnapshot(resource) }))
    .sort((first, second) => {
      const toneScore = { danger: 0, warning: 1, muted: 2, ok: 3 };
      const toneDelta = (toneScore[first.plan.signal.tone] ?? 3) - (toneScore[second.plan.signal.tone] ?? 3);
      if (toneDelta !== 0) {
        return toneDelta;
      }
      return parsePositiveInt(second.resource.attributes?.roundCount) - parsePositiveInt(first.resource.attributes?.roundCount);
    }), [firearms]);
  const serviceHistoryItems = useMemo(() => firearms
    .flatMap((resource) => (resource.attributes?.maintenanceEvents || []).map((event) => ({ event, resource })))
    .sort((first, second) => eventTimestamp(second.event) - eventTimestamp(first.event))
    .slice(0, 8), [firearms]);
  const recordIssueSummary = useMemo(() => {
    const counts = armoryInsights.recordHealthItems.reduce((summary, item) => {
      item.issues.forEach((issue) => {
        summary[issue] = (summary[issue] || 0) + 1;
      });
      return summary;
    }, {});
    return Object.entries(counts)
      .map(([label, count]) => ({ label, count }))
      .sort((first, second) => second.count - first.count);
  }, [armoryInsights.recordHealthItems]);
  const filteredFirearms = useMemo(() => {
    const query = armoryFilters.q.trim().toLowerCase();
    return firearms.filter((item) => {
      const rentalStatus = item.attributes?.rentalStatus || "available";
      const signal = maintenanceSignal(item);
      const matchesView = activeArmorySection === "inventory" ||
        armoryView === "all" ||
        (armoryView === "ready" && rentalStatus === "available" && signal.tone !== "warning" && signal.tone !== "danger") ||
        (armoryView === "issued" && rentalStatus === "checked_out") ||
        (armoryView === "service" && (rentalStatus === "maintenance" || signal.tone === "warning" || signal.tone === "danger"));
      const haystack = [
        firearmName(item),
        item.attributes?.caliber,
        item.attributes?.serialNumber,
        item.attributes?.assetTag,
        item.attributes?.storageLocation,
        item.attributes?.maintenancePlanName,
        item.attributes?.notes
      ].join(" ").toLowerCase();
      return (
        matchesView &&
        (!query || haystack.includes(query))
      );
    });
  }, [activeArmorySection, armoryFilters.q, armoryView, firearms]);
  const maintenanceChecklistItems = checklistItemsFromText(firearmForm.maintenanceChecklist);
  const maintenanceChecklistEditorItems = maintenanceChecklistItems.length > 0 ? maintenanceChecklistItems : [""];
  const serviceUsesDate = firearmForm.serviceTrigger === "date" || firearmForm.serviceTrigger === "date_or_rounds";
  const serviceUsesRounds = firearmForm.serviceTrigger === "rounds" || firearmForm.serviceTrigger === "date_or_rounds";

  useEffect(() => {
    function syncArmorySectionFromHash() {
      setActiveArmorySection(normalizeArmorySection(window.location.hash.replace("#armory-", "")));
    }
    syncArmorySectionFromHash();
    window.addEventListener("hashchange", syncArmorySectionFromHash);
    return () => window.removeEventListener("hashchange", syncArmorySectionFromHash);
  }, []);

  function switchArmorySection(sectionId) {
    const normalized = normalizeArmorySection(sectionId);
    setActiveArmorySection(normalized);
    if (normalized === "inventory") {
      setArmoryView("all");
    } else if (normalized === "issue" && armoryView === "all") {
      setArmoryView("service");
    }
    if (typeof window !== "undefined") {
      const nextHash = `#armory-${normalized}`;
      if (window.location.hash !== nextHash) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
      }
    }
  }

  function updateFirearmForm(patch) {
    setFirearmForm((current) => ({ ...current, ...patch }));
  }

  function updateMaintenanceChecklist(patcher) {
    setFirearmForm((current) => ({
      ...current,
      maintenanceChecklist: checklistItemsToText(patcher(checklistItemsFromText(current.maintenanceChecklist)))
    }));
  }

  function updateMaintenanceChecklistItem(indexToUpdate, value) {
    updateMaintenanceChecklist((items) => {
      const nextItems = items.length > 0 ? [...items] : [""];
      nextItems[indexToUpdate] = value;
      return nextItems;
    });
  }

  function addMaintenanceChecklistItem() {
    updateMaintenanceChecklist((items) => [...items, "New service step"]);
  }

  function removeMaintenanceChecklistItem(indexToRemove) {
    updateMaintenanceChecklist((items) => items.filter((_, index) => index !== indexToRemove));
  }

  function checkoutDraftFor(resourceId) {
    return checkoutDrafts[resourceId] || {
      assignmentType: "member",
      memberId: "",
      staffName: "",
      eventId: "",
      reservationId: ""
    };
  }

  function returnDraftFor(resourceId) {
    return returnDrafts[resourceId] || { rounds: "", malfunctionNotes: "", status: "available" };
  }

  function maintenanceDraftFor(resource) {
    const intervalDays = Number.parseInt(resource.attributes?.maintenanceIntervalDays || 0, 10);
    return maintenanceDrafts[resource.resourceId] || {
      notes: "",
      nextDueAt: addDaysIso(intervalDays)
    };
  }

  function editDraftFor(resource) {
    return editDrafts[resource.resourceId] || firearmDraftFromResource(resource);
  }

  function updateCheckoutDraft(resourceId, patch) {
    setCheckoutDrafts((current) => ({
      ...current,
      [resourceId]: {
        ...checkoutDraftFor(resourceId),
        ...patch
      }
    }));
  }

  function updateReturnDraft(resourceId, patch) {
    setReturnDrafts((current) => ({
      ...current,
      [resourceId]: {
        ...returnDraftFor(resourceId),
        ...patch
      }
    }));
  }

  function updateMaintenanceDraft(resource, patch) {
    setMaintenanceDrafts((current) => ({
      ...current,
      [resource.resourceId]: {
        ...maintenanceDraftFor(resource),
        ...patch
      }
    }));
  }

  function updateEditDraft(resource, patch) {
    setEditDrafts((current) => ({
      ...current,
      [resource.resourceId]: {
        ...editDraftFor(resource),
        ...patch
      }
    }));
  }

  function resetEditDraft(resource) {
    setEditDrafts((current) => ({
      ...current,
      [resource.resourceId]: firearmDraftFromResource(resource)
    }));
  }

  function updateEditMaintenanceChecklist(resource, patcher) {
    setEditDrafts((current) => {
      const draft = current[resource.resourceId] || firearmDraftFromResource(resource);
      return {
        ...current,
        [resource.resourceId]: {
          ...draft,
          maintenanceChecklist: checklistItemsToText(patcher(checklistItemsFromText(draft.maintenanceChecklist)))
        }
      };
    });
  }

  function updateEditMaintenanceChecklistItem(resource, indexToUpdate, value) {
    updateEditMaintenanceChecklist(resource, (items) => {
      const nextItems = items.length > 0 ? [...items] : [""];
      nextItems[indexToUpdate] = value;
      return nextItems;
    });
  }

  function addEditMaintenanceChecklistItem(resource) {
    updateEditMaintenanceChecklist(resource, (items) => [...items, "New service step"]);
  }

  function removeEditMaintenanceChecklistItem(resource, indexToRemove) {
    updateEditMaintenanceChecklist(resource, (items) => items.filter((_, index) => index !== indexToRemove));
  }

  async function submitFirearm(event) {
    event.preventDefault();
    if (!firearmForm.make.trim() && !firearmForm.model.trim() && !firearmForm.assetTag.trim() && !firearmForm.serialNumber.trim()) {
      return;
    }
    await onCreateArmoryItem(buildFirearmPayload(firearmForm));
    setFirearmForm(EMPTY_FIREARM_FORM);
    setAddFirearmFormKey((current) => current + 1);
  }

  function saveFirearmEdits(event, resource) {
    event.preventDefault();
    const draft = editDraftFor(resource);
    if (!draft.make.trim() && !draft.model.trim() && !draft.assetTag.trim() && !draft.serialNumber.trim()) {
      return;
    }
    const existingAttributes = resource.attributes || {};
    const attributes = {
      ...existingAttributes,
      assetClass: "firearm",
      firearmMake: draft.make,
      firearmModel: draft.model,
      caliber: draft.caliber,
      serialNumber: draft.serialNumber,
      assetTag: draft.assetTag,
      storageLocation: draft.storageLocation,
      roundCount: draft.roundCount,
      maintenanceServiceTrigger: draft.serviceTrigger,
      maintenanceDueAt: draft.maintenanceDueAt,
      maintenancePlanName: draft.maintenancePlanName,
      maintenanceIntervalDays: draft.maintenanceIntervalDays,
      maintenanceRoundInterval: draft.maintenanceRoundInterval,
      maintenanceLastServiceRoundCount: existingAttributes.maintenanceLastServiceRoundCount || draft.roundCount,
      maintenanceChecklist: draft.maintenanceChecklist,
      notes: draft.notes
    };
    onUpdateResource(resource.resourceId, {
      name: firearmRecordNameFromDraft(draft),
      attributes
    });
    setEditDrafts((current) => ({
      ...current,
      [resource.resourceId]: {
        ...draft,
        maintenanceChecklist: checklistItemsToText(checklistItemsFromText(draft.maintenanceChecklist))
      }
    }));
  }

  function checkoutFirearm(resource) {
    const draft = checkoutDraftFor(resource.resourceId);
    const member = draft.assignmentType === "member"
      ? members.find((item) => item.memberId === draft.memberId)
      : null;
    const event = draft.assignmentType === "event"
      ? events.find((item) => item.eventId === draft.eventId)
      : null;
    const reservation = draft.assignmentType === "reservation"
      ? reservations.find((item) => item.reservationId === draft.reservationId)
      : null;
    const staffName = draft.staffName.trim();
    if (
      (draft.assignmentType === "member" && !member) ||
      (draft.assignmentType === "event" && !event) ||
      (draft.assignmentType === "reservation" && !reservation) ||
      (draft.assignmentType === "staff" && !staffName)
    ) {
      return;
    }
    const timestamp = new Date().toISOString();
    onUpdateResource(resource.resourceId, {
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: "checked_out",
        checkedOutAssignmentType: draft.assignmentType,
        checkedOutMemberId: member?.memberId || "",
        checkedOutMemberName: member ? (member.name || member.fullName || member.email || "Member") : "",
        checkedOutStaffName: draft.assignmentType === "staff" ? staffName : "",
        checkedOutEventId: event?.eventId || "",
        checkedOutEventName: event ? eventLabel(event) : "",
        checkedOutReservationId: reservation?.reservationId || "",
        checkedOutReservationLabel: reservation ? reservationLabel(reservation) : "",
        checkedOutAt: timestamp
      }
    });
    setCheckoutDrafts((current) => ({
      ...current,
      [resource.resourceId]: checkoutDraftFor("")
    }));
  }

  function returnFirearm(resource) {
    const draft = returnDraftFor(resource.resourceId);
    const rounds = Number.parseInt(draft.rounds || "0", 10);
    const safeRounds = Number.isFinite(rounds) && rounds > 0 ? rounds : 0;
    const previousRoundCount = Number(resource.attributes?.roundCount || 0);
    const timestamp = new Date().toISOString();
    const event = {
      action: draft.status === "maintenance" ? "returned_to_maintenance" : "returned",
      title: draft.status === "maintenance" ? "Returned for maintenance" : "Returned to armory",
      note: draft.malfunctionNotes || `${safeRounds} rounds fired.`,
      source: "armory",
      createdAt: timestamp
    };

    onUpdateResource(resource.resourceId, {
      status: draft.status === "maintenance" ? "maintenance" : "open",
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: draft.status,
        checkedOutAssignmentType: "",
        checkedOutMemberId: "",
        checkedOutMemberName: "",
        checkedOutStaffName: "",
        checkedOutEventId: "",
        checkedOutEventName: "",
        checkedOutReservationId: "",
        checkedOutReservationLabel: "",
        checkedOutAt: "",
        roundCount: previousRoundCount + safeRounds,
        lastReturnRounds: safeRounds,
        lastMalfunctionNotes: draft.malfunctionNotes,
        maintenanceLastActionAt: timestamp,
        maintenanceLastActionSource: "armory",
        maintenanceEvents: [event, ...(resource.attributes?.maintenanceEvents || [])].slice(0, 20)
      }
    });
    setReturnDrafts((current) => ({
      ...current,
      [resource.resourceId]: { rounds: "", malfunctionNotes: "", status: "available" }
    }));
  }

  function sendToMaintenance(resource) {
    const timestamp = new Date().toISOString();
    const event = {
      action: "sent_to_maintenance",
      title: "Sent to maintenance",
      note: "Armory item pulled from service.",
      source: "armory",
      createdAt: timestamp
    };
    onUpdateResource(resource.resourceId, {
      status: "maintenance",
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: "maintenance",
        maintenanceLastActionAt: timestamp,
        maintenanceLastActionSource: "armory",
        maintenanceEvents: [event, ...(resource.attributes?.maintenanceEvents || [])].slice(0, 20)
      }
    });
  }

  function completeMaintenance(resource) {
    const draft = maintenanceDraftFor(resource);
    const timestamp = new Date().toISOString();
    const event = {
      action: "maintenance_completed",
      title: "Maintenance completed",
      note: draft.notes || "Armory maintenance completed.",
      source: "armory",
      createdAt: timestamp
    };
    onUpdateResource(resource.resourceId, {
      status: "open",
      attributes: {
        ...(resource.attributes || {}),
        rentalStatus: "available",
        maintenanceLastCompletedAt: timestamp,
        maintenanceDueAt: draft.nextDueAt || resource.attributes?.maintenanceDueAt || "",
        maintenanceLastServiceRoundCount: resource.attributes?.roundCount || 0,
        maintenanceNotes: draft.notes || resource.attributes?.maintenanceNotes || "",
        maintenanceLastActionAt: timestamp,
        maintenanceLastActionSource: "armory",
        maintenanceEvents: [event, ...(resource.attributes?.maintenanceEvents || [])].slice(0, 20)
      }
    });
    setMaintenanceDrafts((current) => ({
      ...current,
      [resource.resourceId]: {
        notes: "",
        nextDueAt: addDaysIso(resource.attributes?.maintenanceIntervalDays || 0)
      }
    }));
  }

  return (
    <div className="ops-page armory-page">
      {loading && firearms.length === 0 && <div className="status-strip">Loading armory records...</div>}

      <section className="armory-command-center">
        <div className="armory-command-copy">
          <span className="section-kicker">Armory</span>
          <h3>Checkout-ready firearms</h3>
          <p>{commandSummary} Track custody, round count, storage, and service dates without mixing firearms into bay setup.</p>
        </div>
        <dl className="armory-command-metrics" aria-label="Armory status">
          <div>
            <dt>Ready now</dt>
            <dd>{issueReadyFirearms.length}</dd>
          </div>
          <div>
            <dt>Issued</dt>
            <dd>{checkedOutFirearms.length}</dd>
          </div>
          <div>
            <dt>Service watch</dt>
            <dd>{attentionCount}</dd>
          </div>
          <div>
            <dt>Total records</dt>
            <dd>{firearms.length}</dd>
          </div>
        </dl>
      </section>

      <nav className="console-section-nav page-section-nav armory-section-tabs" aria-label="Armory sections">
        {armorySections.map((section) => (
          <button
            type="button"
            className={activeArmorySection === section.id ? "is-active" : ""}
            key={section.id}
            onClick={() => switchArmorySection(section.id)}
          >
            <span>{section.label}</span>
            <small>{section.meta}</small>
          </button>
        ))}
      </nav>

      <section className="membership-section-overview armory-section-overview">
        <div>
          <span className="section-kicker">{activeSectionCopy.kicker}</span>
          <h3>{activeSectionCopy.title}</h3>
          <p>{activeSectionCopy.description}</p>
        </div>
        <div className="membership-context-metrics armory-section-metrics">
          <div>
            <span>Ready now</span>
            <strong>{issueReadyFirearms.length}</strong>
            <small>{firearms.length ? "safe to issue" : "no records"}</small>
          </div>
          <div>
            <span>Service watch</span>
            <strong>{attentionCount}</strong>
            <small>{dueSoonFirearms.length} due or overdue</small>
          </div>
          <div>
            <span>Checked out</span>
            <strong>{checkedOutFirearms.length}</strong>
            <small>{retiredFirearms.length} retired</small>
          </div>
        </div>
      </section>

      {activeArmorySection === "overview" && (
      <section className="armory-intelligence-panel">
        <div className="panel-header armory-intelligence-header">
          <div>
            <span className="section-kicker">Armory intelligence</span>
            <h2>Where the armory needs work today</h2>
            <p>A short command view. Use the tabs for the actual counter workflow, service review, or record cleanup.</p>
          </div>
          <div className="armory-intelligence-callout">
            <strong>{attentionCount > 0 ? "Hold and inspect" : "Issue desk clear"}</strong>
            <span>
              {attentionCount > 0
                ? `${attentionCount} firearm${attentionCount === 1 ? "" : "s"} should not be treated as routine issue.`
                : "No service-blocking signals are active right now."}
            </span>
          </div>
        </div>

        <div className="armory-insight-grid">
          <div className="armory-insight-panel armory-readiness-panel">
            <div className="armory-insight-title">
              <strong>Readiness mix</strong>
              <span>{firearms.length} total</span>
            </div>
            <div className="armory-mix-bar" aria-label="Armory readiness mix">
              {readinessMix.map((item) => (
                <span
                  className={`armory-mix-segment armory-mix-${item.key}`}
                  key={item.key}
                  style={{ width: `${readinessPercent(item.count, firearms.length)}%` }}
                  title={`${item.label}: ${item.count}`}
                />
              ))}
            </div>
            <div className="armory-mix-legend">
              {readinessMix.map((item) => (
                <div key={item.key}>
                  <span className={`armory-mix-dot armory-mix-${item.key}`} />
                  <strong>{item.count}</strong>
                  <small>{item.label}</small>
                </div>
              ))}
            </div>
          </div>

          <div className="armory-insight-panel">
            <div className="armory-insight-title">
              <strong>Counter work</strong>
              <span>{issueReadyFirearms.length} ready</span>
            </div>
            <div className="armory-insight-list">
              <div className="armory-insight-row">
                <span>
                  <strong>{attentionCount} held for service</strong>
                  <small>Use Issue desk to check out ready firearms or pull blocked firearms from circulation.</small>
                </span>
                <em>Issue</em>
              </div>
              <div className="armory-insight-row">
                <span>
                  <strong>{checkedOutFirearms.length} checked out</strong>
                  <small>Use returns to capture rounds fired and malfunctions before the firearm goes back out.</small>
                </span>
                <em>Return</em>
              </div>
            </div>
          </div>

          <div className="armory-insight-panel armory-round-panel">
            <div className="armory-insight-title">
              <strong>Service direction</strong>
              <span>{dueSoonFirearms.length} due</span>
            </div>
            <div className="armory-round-summary">
              <div>
                <strong>{averageRounds.toLocaleString()}</strong>
                <span>average rounds</span>
              </div>
              <div>
                <strong>{armoryInsights.roundRuleCount}/{firearms.length || 0}</strong>
                <span>round rules set</span>
              </div>
            </div>
            <div className="armory-insight-empty">
              Service tab owns maintenance rules, reliability notes, and service history. Inventory owns missing fields and record edits.
            </div>
          </div>
        </div>
      </section>
      )}

      {activeArmorySection === "inventory" && (
      <div className="armory-add-row">
        <TaskDisclosure key={addFirearmFormKey} actionLabel="Add firearm" title="Add firearm" description="Create a range-owned firearm record for checkout, return, and maintenance tracking.">
          <form className="member-form armory-form" onSubmit={submitFirearm}>
            <div className="armory-form-intro">
              <strong>Record enough for staff to identify, issue, and service the firearm.</strong>
              <span>Make and model are preferred. Asset tag or serial number can stand in when the record is being created from a paper log.</span>
            </div>
            <div className="armory-form-sections">
              <fieldset className="armory-form-section armory-form-section-primary">
                <legend>Identity</legend>
                <div className="armory-form-grid">
                  <label>
                    Make
                    <input value={firearmForm.make} onChange={(event) => updateFirearmForm({ make: event.target.value })} placeholder="Ruger" />
                  </label>
                  <label>
                    Model
                    <input value={firearmForm.model} onChange={(event) => updateFirearmForm({ model: event.target.value })} placeholder="Precision Rifle" />
                  </label>
                  <label>
                    Caliber
                    <input value={firearmForm.caliber} onChange={(event) => updateFirearmForm({ caliber: event.target.value })} placeholder="6.5 Creedmoor" />
                  </label>
                  <label>
                    Serial number
                    <input value={firearmForm.serialNumber} onChange={(event) => updateFirearmForm({ serialNumber: event.target.value })} placeholder="Manufacturer serial" />
                  </label>
                </div>
              </fieldset>

              <fieldset className="armory-form-section">
                <legend>Custody</legend>
                <div className="armory-form-grid armory-form-grid-compact">
                  <label>
                    Asset tag
                    <input value={firearmForm.assetTag} onChange={(event) => updateFirearmForm({ assetTag: event.target.value })} placeholder="LLR-RFL-001" />
                  </label>
                  <label>
                    Storage
                    <input value={firearmForm.storageLocation} onChange={(event) => updateFirearmForm({ storageLocation: event.target.value })} placeholder="Safe A" />
                  </label>
                  <label>
                    Starting round count
                    <input type="number" min="0" value={firearmForm.roundCount} onChange={(event) => updateFirearmForm({ roundCount: event.target.value })} />
                  </label>
                </div>
              </fieldset>

              <fieldset className="armory-form-section">
                <legend>Service</legend>
                <div className="armory-service-mode-grid">
                  <label className="armory-service-mode">
                    Service trigger
                    <select
                      value={firearmForm.serviceTrigger}
                      onChange={(event) => updateFirearmForm({ serviceTrigger: event.target.value })}
                    >
                      <option value="date_or_rounds">Date or round count</option>
                      <option value="rounds">Round count only</option>
                      <option value="date">Calendar only</option>
                      <option value="manual">Manual service only</option>
                    </select>
                    <small>Use “date or round count” for whichever comes first.</small>
                  </label>
                  <div className="armory-service-rule-card">
                    <span>Service status rule</span>
                    <strong>
                      {firearmForm.serviceTrigger === "date_or_rounds"
                        ? "Due when either limit is reached"
                        : firearmForm.serviceTrigger === "rounds"
                          ? "Due from rounds fired at return"
                          : firearmForm.serviceTrigger === "date"
                            ? "Due from calendar schedule"
                            : "Owner pulls it into service manually"}
                    </strong>
                  </div>
                </div>
                <div className="armory-form-grid armory-form-grid-compact">
                  {serviceUsesDate && (
                    <>
                      <label>
                        Next service date
                        <input type="date" value={firearmForm.maintenanceDueAt} onChange={(event) => updateFirearmForm({ maintenanceDueAt: event.target.value })} />
                      </label>
                      <label>
                        Repeat every
                        <input type="number" min="0" value={firearmForm.maintenanceIntervalDays} onChange={(event) => updateFirearmForm({ maintenanceIntervalDays: event.target.value })} placeholder="Days" />
                      </label>
                    </>
                  )}
                  {serviceUsesRounds && (
                    <label>
                      Service every
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          min="0"
                          value={firearmForm.maintenanceRoundInterval}
                          onChange={(event) => updateFirearmForm({ maintenanceRoundInterval: event.target.value })}
                          placeholder="500"
                        />
                        <span>rounds</span>
                      </div>
                    </label>
                  )}
                  {firearmForm.serviceTrigger === "manual" && (
                    <div className="armory-service-manual-note">
                      No automatic service due state will be calculated. Staff can still send the firearm to maintenance from the ledger.
                    </div>
                  )}
                </div>
              </fieldset>

              <div className="armory-form-secondary-row">
                <details className="armory-secondary-details">
                  <summary>
                    <strong>Service checklist</strong>
                    <span>Inspection plan and repeatable maintenance steps.</span>
                  </summary>
                  <div>
                    <label>
                      Plan name
                      <input value={firearmForm.maintenancePlanName} onChange={(event) => updateFirearmForm({ maintenancePlanName: event.target.value })} placeholder="Firearm inspection" />
                    </label>
                    <div className="armory-checklist-editor">
                      <div className="armory-checklist-editor-header">
                        <strong>Checklist steps</strong>
                        <span>{maintenanceChecklistItems.length} configured</span>
                      </div>
                      {maintenanceChecklistEditorItems.map((item, index) => (
                        <div className="armory-checklist-row" key={`maintenance-check-${index}`}>
                          <label>
                            Step {index + 1}
                            <input
                              value={item}
                              onChange={(event) => updateMaintenanceChecklistItem(index, event.target.value)}
                              placeholder="Example: Clean and inspect bore"
                            />
                          </label>
                          <button
                            type="button"
                            className="secondary-button"
                            disabled={maintenanceChecklistItems.length === 0}
                            onClick={() => removeMaintenanceChecklistItem(index)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button type="button" className="secondary-button" onClick={addMaintenanceChecklistItem}>
                        Add step
                      </button>
                    </div>
                  </div>
                </details>

                <details className="armory-secondary-details">
                  <summary>
                    <strong>Staff notes</strong>
                    <span>Optics, zero, suppressor mount, or handling context.</span>
                  </summary>
                  <div>
                    <label>
                      Notes
                      <textarea rows={4} value={firearmForm.notes} onChange={(event) => updateFirearmForm({ notes: event.target.value })} placeholder="Optic, suppressor mount, zero, or staff handling notes" />
                    </label>
                  </div>
                </details>
              </div>
            </div>
            <div className="armory-form-actions">
              <span>Checkout status starts as available.</span>
              <button type="submit" disabled={savingArmoryItem}>
                {savingArmoryItem ? "Adding..." : "Create firearm record"}
              </button>
            </div>
          </form>
        </TaskDisclosure>
      </div>
      )}

      {activeArmorySection === "inventory" && (
      <section className="armory-inventory-health-panel">
        <div className="armory-inventory-health-copy">
          <span className="section-kicker">Record health</span>
          <h2>Fields that keep records trustworthy</h2>
          <p>Inventory owns the permanent firearm record. Counter actions and service decisions stay in their own tabs.</p>
        </div>
        <div className="armory-inventory-health-body">
          {recordIssueSummary.length > 0 ? (
            <>
              <div className="armory-tag-list">
                {recordIssueSummary.map((issue) => (
                  <span key={issue.label}>
                    <strong>{issue.count}</strong>
                    {issue.label}
                  </span>
                ))}
              </div>
              <div className="armory-review-list">
                {armoryInsights.recordHealthItems.slice(0, 4).map((item) => (
                  <div className="armory-review-row" key={item.resource.resourceId}>
                    <span>
                      <strong>{firearmName(item.resource)}</strong>
                      <small>{item.issues.join(", ")}</small>
                    </span>
                    <em>Edit</em>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="armory-review-empty">Every firearm has the core identity, custody, round count, service rule, and checklist fields needed for daily operation.</div>
          )}
        </div>
      </section>
      )}

      {(activeArmorySection === "issue" || activeArmorySection === "inventory") && (
      <section className="panel ops-support-panel">
        <div className="panel-header">
          <div>
            <h2>{activeArmorySection === "inventory" ? "Inventory records" : "Issue desk"}</h2>
            <p>
              {activeArmorySection === "inventory"
                ? "Review every firearm record and open edit only when identity, custody, service, or notes need cleanup."
                : "Work the exceptions first: issued firearms, service-due records, then available inventory."}
            </p>
          </div>
          {firearms.length > 0 && (
            <div className="armory-panel-summary">
              <strong>{filteredFirearms.length}</strong>
              <span>{filteredFirearms.length === 1 ? "record shown" : "records shown"}</span>
            </div>
          )}
        </div>
        {firearms.length > 0 && (
          <div className="armory-ledger-controls">
            {activeArmorySection === "issue" && (
            <div className="armory-view-tabs" role="tablist" aria-label="Armory ledger view">
              {armoryViewOptions.map((option) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={armoryView === option.key}
                  className={armoryView === option.key ? "is-active" : ""}
                  key={option.key}
                  onClick={() => setArmoryView(option.key)}
                >
                  <span>{option.label}</span>
                  <strong>{option.count}</strong>
                </button>
              ))}
            </div>
            )}
            <div className="armory-filter-grid">
              <label>
                {activeArmorySection === "inventory" ? "Search records" : "Search this view"}
                <input
                  value={armoryFilters.q}
                  onChange={(event) => setArmoryFilters((current) => ({ ...current, q: event.target.value }))}
                  placeholder="Make, model, serial, tag, storage"
                />
              </label>
              <button
                type="button"
                className="secondary-button"
                disabled={!armoryFilters.q}
                onClick={() => setArmoryFilters({ q: "" })}
              >
                Clear search
              </button>
            </div>
          </div>
        )}

        <div className="maintenance-queue-list">
          {filteredFirearms.map((resource) => {
            const rentalStatus = resource.attributes?.rentalStatus || "available";
            const isUpdating = updatingResourceId === resource.resourceId;
            const checkoutDraft = checkoutDraftFor(resource.resourceId);
            const returnDraft = returnDraftFor(resource.resourceId);
            const maintenanceDraft = maintenanceDraftFor(resource);
            const checkedOut = rentalStatus === "checked_out";
            const inMaintenance = rentalStatus === "maintenance";
            const latestMaintenanceEvent = resource.attributes?.maintenanceEvents?.[0];
            const serviceSignal = maintenanceSignal(resource);
            const editDraft = editDraftFor(resource);
            const editChecklistItems = checklistItemsFromText(editDraft.maintenanceChecklist);
            const editChecklistEditorItems = editChecklistItems.length > 0 ? editChecklistItems : [""];
            const editServiceUsesDate = editDraft.serviceTrigger === "date" || editDraft.serviceTrigger === "date_or_rounds";
            const editServiceUsesRounds = editDraft.serviceTrigger === "rounds" || editDraft.serviceTrigger === "date_or_rounds";
            return (
              <article
                className={`maintenance-queue-card armory-card armory-card-${rentalStatusClass(rentalStatus)} ${activeArmorySection === "inventory" ? "armory-record-card" : ""}`}
                key={resource.resourceId}
              >
                <div className="maintenance-card-main">
                  <div className="maintenance-card-title">
                    <div>
                      <strong>{firearmName(resource)}</strong>
                      <span>{resource.attributes?.caliber || "No caliber"} / SN {resource.attributes?.serialNumber || "not set"} / {resource.attributes?.assetTag || "no asset tag"}</span>
                    </div>
                    <span className={rentalStatus === "maintenance" ? "status-badge status-warning" : "status-badge status-muted"}>
                      {statusLabel(rentalStatus)}
                    </span>
                  </div>

                  <dl className="maintenance-facts">
                    <div>
                      <dt>Round count</dt>
                      <dd>{Number(resource.attributes?.roundCount || 0).toLocaleString()}</dd>
                    </div>
                    <div>
                      <dt>Storage</dt>
                      <dd>{resource.attributes?.storageLocation || "Not set"}</dd>
                    </div>
                    <div>
                      <dt>Maintenance due</dt>
                      <dd className={`armory-service-${serviceSignal.tone}`}>{serviceSignal.label}</dd>
                    </div>
                  </dl>

                  {checkedOut && (
                    <div className="maintenance-log-preview armory-assignment-preview">
                      <strong>Checked out</strong>
                      <span>{checkoutAssignmentLabel(resource)}</span>
                      <span>{dateOnly(resource.attributes?.checkedOutAt)}</span>
                    </div>
                  )}
                  {latestMaintenanceEvent && (
                    <div className="maintenance-log-preview">
                      <strong>{latestMaintenanceEvent.title || statusLabel(latestMaintenanceEvent.action)}</strong>
                      <span>{latestMaintenanceEvent.note || "No notes recorded"}</span>
                      <span>{dateOnly(latestMaintenanceEvent.createdAt)}</span>
                    </div>
                  )}
                </div>

                {activeArmorySection === "issue" && (
                <div className="maintenance-card-actions">
                  {checkedOut ? (
                    <TaskDisclosure actionLabel="Return" title="Return firearm" description="Record rounds fired and any failures before the item goes back to service or maintenance.">
                      <div className="stacked-form">
                        <div className="form-row">
                          <label>
                            Rounds fired
                            <input
                              type="number"
                              min="0"
                              value={returnDraft.rounds}
                              onChange={(event) => updateReturnDraft(resource.resourceId, { rounds: event.target.value })}
                            />
                          </label>
                          <label>
                            Return status
                            <select
                              value={returnDraft.status}
                              onChange={(event) => updateReturnDraft(resource.resourceId, { status: event.target.value })}
                            >
                              <option value="available">Available</option>
                              <option value="maintenance">Needs maintenance</option>
                            </select>
                          </label>
                        </div>
                        <label>
                          Failures or malfunctions
                          <textarea
                            rows={3}
                            value={returnDraft.malfunctionNotes}
                            onChange={(event) => updateReturnDraft(resource.resourceId, { malfunctionNotes: event.target.value })}
                            placeholder="Failure to feed, optic issue, damage, or no issues"
                          />
                        </label>
                        <button type="button" disabled={isUpdating} onClick={() => returnFirearm(resource)}>Record return</button>
                      </div>
                    </TaskDisclosure>
                  ) : inMaintenance ? (
                    <div className="maintenance-log-preview maintenance-log-preview-muted">
                      <strong>Checkout paused</strong>
                      <span>Complete maintenance before this firearm can be checked out again.</span>
                    </div>
                  ) : (
                    <TaskDisclosure actionLabel="Check out" title="Check out firearm" description="Assign this firearm to a member or staff-controlled session.">
                      <div className="stacked-form">
                        <label>
                          Assignment
                          <select
                            value={checkoutDraft.assignmentType}
                            onChange={(event) => updateCheckoutDraft(resource.resourceId, {
                              assignmentType: event.target.value,
                              memberId: "",
                              staffName: "",
                              eventId: "",
                              reservationId: ""
                            })}
                          >
                            <option value="member">Member</option>
                            <option value="staff">Staff controlled</option>
                            <option value="event">Event or class</option>
                            <option value="reservation">Reservation</option>
                          </select>
                        </label>
                        {checkoutDraft.assignmentType === "member" && (
                        <label>
                          Member
                          <select
                            value={checkoutDraft.memberId}
                            onChange={(event) => updateCheckoutDraft(resource.resourceId, { memberId: event.target.value })}
                          >
                            <option value="">Select member</option>
                            {members.map((member) => (
                              <option value={member.memberId} key={member.memberId}>
                                {member.name || member.fullName || member.email || member.memberId}
                              </option>
                            ))}
                          </select>
                        </label>
                        )}
                        {checkoutDraft.assignmentType === "staff" && (
                          <label>
                            Staff or RSO
                            <input
                              value={checkoutDraft.staffName}
                              onChange={(event) => updateCheckoutDraft(resource.resourceId, { staffName: event.target.value })}
                              placeholder="RSO or staff name"
                            />
                          </label>
                        )}
                        {checkoutDraft.assignmentType === "event" && (
                          <label>
                            Event or class
                            <select
                              value={checkoutDraft.eventId}
                              onChange={(event) => updateCheckoutDraft(resource.resourceId, { eventId: event.target.value })}
                            >
                              <option value="">Select event</option>
                              {events.map((event) => (
                                <option value={event.eventId} key={event.eventId}>{eventLabel(event)}</option>
                              ))}
                            </select>
                          </label>
                        )}
                        {checkoutDraft.assignmentType === "reservation" && (
                          <label>
                            Reservation
                            <select
                              value={checkoutDraft.reservationId}
                              onChange={(event) => updateCheckoutDraft(resource.resourceId, { reservationId: event.target.value })}
                            >
                              <option value="">Select reservation</option>
                              {reservations.map((reservation) => (
                                <option value={reservation.reservationId} key={reservation.reservationId}>
                                  {reservationLabel(reservation)}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        <button
                          type="button"
                          disabled={
                            isUpdating ||
                            (checkoutDraft.assignmentType === "member" && !checkoutDraft.memberId) ||
                            (checkoutDraft.assignmentType === "staff" && !checkoutDraft.staffName.trim()) ||
                            (checkoutDraft.assignmentType === "event" && !checkoutDraft.eventId) ||
                            (checkoutDraft.assignmentType === "reservation" && !checkoutDraft.reservationId)
                          }
                          onClick={() => checkoutFirearm(resource)}
                        >
                          Check out
                        </button>
                      </div>
                    </TaskDisclosure>
                  )}
                  {inMaintenance ? (
                    <TaskDisclosure actionLabel="Complete service" title="Complete maintenance" description="Record service notes and advance the next due date from this firearm's maintenance plan.">
                      <div className="stacked-form">
                        <label>
                          Service notes
                          <textarea
                            rows={3}
                            value={maintenanceDraft.notes}
                            onChange={(event) => updateMaintenanceDraft(resource, { notes: event.target.value })}
                            placeholder="Cleaned, inspected, replaced part, zero checked"
                          />
                        </label>
                        <label>
                          Next due date
                          <input
                            type="date"
                            value={maintenanceDraft.nextDueAt}
                            onChange={(event) => updateMaintenanceDraft(resource, { nextDueAt: event.target.value })}
                          />
                        </label>
                        <button type="button" disabled={isUpdating} onClick={() => completeMaintenance(resource)}>
                          Complete maintenance
                        </button>
                      </div>
                    </TaskDisclosure>
                  ) : (
                    <button type="button" className="secondary-button" disabled={isUpdating || checkedOut} onClick={() => sendToMaintenance(resource)}>
                      Send to maintenance
                    </button>
                  )}
                </div>
                )}

                {activeArmorySection === "inventory" && (
                <details className="armory-edit-disclosure">
                  <summary>Edit record</summary>
                  <div className="armory-edit-body">
                  <form className="member-form armory-form armory-edit-form" onSubmit={(event) => saveFirearmEdits(event, resource)}>
                    <div className="armory-form-intro">
                      <strong>Keep this firearm record aligned with the physical asset.</strong>
                      <span>Edits preserve checkout status, service history, and return logs.</span>
                    </div>
                    <div className="armory-form-sections">
                      <fieldset className="armory-form-section">
                        <legend>Identity</legend>
                        <div className="armory-form-grid">
                          <label>
                            Make
                            <input value={editDraft.make} onChange={(event) => updateEditDraft(resource, { make: event.target.value })} placeholder="Ruger" />
                          </label>
                          <label>
                            Model
                            <input value={editDraft.model} onChange={(event) => updateEditDraft(resource, { model: event.target.value })} placeholder="Precision Rifle" />
                          </label>
                          <label>
                            Caliber
                            <input value={editDraft.caliber} onChange={(event) => updateEditDraft(resource, { caliber: event.target.value })} placeholder="6.5 Creedmoor" />
                          </label>
                          <label>
                            Serial number
                            <input value={editDraft.serialNumber} onChange={(event) => updateEditDraft(resource, { serialNumber: event.target.value })} placeholder="Manufacturer serial" />
                          </label>
                        </div>
                      </fieldset>

                      <fieldset className="armory-form-section">
                        <legend>Custody</legend>
                        <div className="armory-form-grid armory-form-grid-compact">
                          <label>
                            Asset tag
                            <input value={editDraft.assetTag} onChange={(event) => updateEditDraft(resource, { assetTag: event.target.value })} placeholder="LLR-RFL-001" />
                          </label>
                          <label>
                            Storage
                            <input value={editDraft.storageLocation} onChange={(event) => updateEditDraft(resource, { storageLocation: event.target.value })} placeholder="Safe A" />
                          </label>
                          <label>
                            Round count
                            <input type="number" min="0" value={editDraft.roundCount} onChange={(event) => updateEditDraft(resource, { roundCount: event.target.value })} />
                          </label>
                        </div>
                      </fieldset>

                      <fieldset className="armory-form-section">
                        <legend>Service</legend>
                        <div className="armory-service-mode-grid">
                          <label className="armory-service-mode">
                            Service trigger
                            <select
                              value={editDraft.serviceTrigger}
                              onChange={(event) => updateEditDraft(resource, { serviceTrigger: event.target.value })}
                            >
                              <option value="date_or_rounds">Date or round count</option>
                              <option value="rounds">Round count only</option>
                              <option value="date">Calendar only</option>
                              <option value="manual">Manual service only</option>
                            </select>
                            <small>Use “date or round count” for whichever comes first.</small>
                          </label>
                          <div className="armory-service-rule-card">
                            <span>Service status rule</span>
                            <strong>
                              {editDraft.serviceTrigger === "date_or_rounds"
                                ? "Due when either limit is reached"
                                : editDraft.serviceTrigger === "rounds"
                                  ? "Due from rounds fired at return"
                                  : editDraft.serviceTrigger === "date"
                                    ? "Due from calendar schedule"
                                    : "Owner pulls it into service manually"}
                            </strong>
                          </div>
                        </div>
                        <div className="armory-form-grid armory-form-grid-compact">
                          {editServiceUsesDate && (
                            <>
                              <label>
                                Next service date
                                <input type="date" value={editDraft.maintenanceDueAt} onChange={(event) => updateEditDraft(resource, { maintenanceDueAt: event.target.value })} />
                              </label>
                              <label>
                                Repeat every
                                <input type="number" min="0" value={editDraft.maintenanceIntervalDays} onChange={(event) => updateEditDraft(resource, { maintenanceIntervalDays: event.target.value })} placeholder="Days" />
                              </label>
                            </>
                          )}
                          {editServiceUsesRounds && (
                            <label>
                              Service every
                              <div className="input-with-suffix">
                                <input
                                  type="number"
                                  min="0"
                                  value={editDraft.maintenanceRoundInterval}
                                  onChange={(event) => updateEditDraft(resource, { maintenanceRoundInterval: event.target.value })}
                                  placeholder="500"
                                />
                                <span>rounds</span>
                              </div>
                            </label>
                          )}
                          {editDraft.serviceTrigger === "manual" && (
                            <div className="armory-service-manual-note">
                              No automatic service due state will be calculated. Staff can still send the firearm to maintenance from the ledger.
                            </div>
                          )}
                        </div>
                      </fieldset>

                      <div className="armory-form-secondary-row">
                        <details className="armory-secondary-details">
                          <summary>
                            <strong>Service checklist</strong>
                            <span>Inspection plan and repeatable maintenance steps.</span>
                          </summary>
                          <div>
                            <label>
                              Plan name
                              <input value={editDraft.maintenancePlanName} onChange={(event) => updateEditDraft(resource, { maintenancePlanName: event.target.value })} placeholder="Firearm inspection" />
                            </label>
                            <div className="armory-checklist-editor">
                              <div className="armory-checklist-editor-header">
                                <strong>Checklist steps</strong>
                                <span>{editChecklistItems.length} configured</span>
                              </div>
                              {editChecklistEditorItems.map((item, index) => (
                                <div className="armory-checklist-row" key={`${resource.resourceId}-maintenance-check-${index}`}>
                                  <label>
                                    Step {index + 1}
                                    <input
                                      value={item}
                                      onChange={(event) => updateEditMaintenanceChecklistItem(resource, index, event.target.value)}
                                      placeholder="Example: Clean and inspect bore"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    className="secondary-button"
                                    disabled={editChecklistItems.length === 0}
                                    onClick={() => removeEditMaintenanceChecklistItem(resource, index)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button type="button" className="secondary-button" onClick={() => addEditMaintenanceChecklistItem(resource)}>
                                Add step
                              </button>
                            </div>
                          </div>
                        </details>

                        <details className="armory-secondary-details">
                          <summary>
                            <strong>Staff notes</strong>
                            <span>Optics, zero, suppressor mount, or handling context.</span>
                          </summary>
                          <div>
                            <label>
                              Notes
                              <textarea rows={4} value={editDraft.notes} onChange={(event) => updateEditDraft(resource, { notes: event.target.value })} placeholder="Optic, suppressor mount, zero, or staff handling notes" />
                            </label>
                          </div>
                        </details>
                      </div>
                    </div>
                    <div className="armory-form-actions">
                      <span>Checkout status and service history stay intact.</span>
                      <div className="row-actions">
                        <button type="button" className="secondary-button" disabled={isUpdating} onClick={() => resetEditDraft(resource)}>
                          Reset
                        </button>
                        <button type="submit" disabled={isUpdating}>
                          {isUpdating ? "Saving..." : "Save record"}
                        </button>
                      </div>
                    </div>
                  </form>
                  </div>
                </details>
                )}
              </article>
            );
          })}
        </div>

        {firearms.length === 0 && (
          <div className="empty-state armory-empty-state">
            <strong>Build the first checkout-ready record</strong>
            <p>Add one firearm with identity, storage, round count, and service cadence. After that, staff can issue it, record returns, and pull it from service when needed.</p>
            <div>
              <span>Identity</span>
              <span>Storage</span>
              <span>Service plan</span>
              <span>Checkout history</span>
            </div>
          </div>
        )}
        {firearms.length > 0 && filteredFirearms.length === 0 && (
          <div className="empty-state">No firearms match the current filters.</div>
        )}
      </section>
      )}

      {activeArmorySection === "service" && (
      <section className="armory-review-panel">
        <div className="armory-review-header">
          <div>
            <span className="section-kicker">Armory review</span>
            <h2>Service, rounds, and reliability</h2>
            <p>Maintenance rules, round pressure, reliability notes, and completed work. Permanent record cleanup lives in Inventory.</p>
          </div>
          <div className="armory-review-score">
            <strong>{dueSoonFirearms.length}</strong>
            <span>due or overdue</span>
          </div>
        </div>

        <div className="armory-review-grid">
          <div className="armory-review-section armory-review-wide">
            <div className="armory-review-title">
              <strong>Service and rounds</strong>
              <span>{armoryInsights.roundRuleCount} round-based {armoryInsights.roundRuleCount === 1 ? "rule" : "rules"}</span>
            </div>
            {servicePlanItems.length > 0 ? (
              <div className="armory-service-table">
                {servicePlanItems.slice(0, 6).map(({ resource, plan }) => (
                  <div className="armory-service-row" key={resource.resourceId}>
                    <span>
                      <strong>{firearmName(resource)}</strong>
                      <small>{plan.ruleLabel} / {plan.checklistCount || "No"} checklist {plan.checklistCount === 1 ? "step" : "steps"}</small>
                    </span>
                    <span>
                      <strong>{plan.roundInterval > 0 ? `${plan.roundsSinceService.toLocaleString()} / ${plan.roundInterval.toLocaleString()}` : "No round rule"}</strong>
                      <small>{plan.roundInterval > 0 ? "rounds since service" : "Set a round limit to forecast wear"}</small>
                    </span>
                    <span>
                      <strong>{plan.dueAt ? dateOnly(plan.dueAt) : "No date rule"}</strong>
                      <small>next calendar service</small>
                    </span>
                    <em className={`armory-service-${plan.signal.tone}`}>{plan.signal.label}</em>
                  </div>
                ))}
              </div>
            ) : (
              <div className="armory-review-empty">Add firearm records to start service planning.</div>
            )}
          </div>

          <div className="armory-review-section armory-review-wide">
            <div className="armory-review-title">
              <strong>Reliability</strong>
              <span>{armoryInsights.reliabilityItems.length || "No active flags"}</span>
            </div>
            {armoryInsights.reliabilityItems.length > 0 ? (
              <div className="armory-review-list">
                {armoryInsights.reliabilityItems.map((item) => (
                  <div className="armory-review-row" key={item.resource.resourceId}>
                    <span>
                      <strong>{firearmName(item.resource)}</strong>
                      <small>{item.note}</small>
                    </span>
                    <em>Inspect</em>
                  </div>
                ))}
              </div>
            ) : (
              <div className="armory-review-empty">No notes currently mention malfunctions, damage, optics, zero, or part replacement.</div>
            )}
          </div>

          <div className="armory-review-section armory-review-wide">
            <div className="armory-review-title">
              <strong>Service history</strong>
              <span>{serviceHistoryItems.length ? "Latest work" : "No work logged"}</span>
            </div>
            {serviceHistoryItems.length > 0 ? (
              <div className="armory-history-list">
                {serviceHistoryItems.map(({ event, resource }, index) => (
                  <div className="armory-history-row" key={`${resource.resourceId}-${event.createdAt || index}`}>
                    <span>
                      <strong>{serviceEventLabel(event)}</strong>
                      <small>{firearmName(resource)} / {dateOnly(event.createdAt)}</small>
                    </span>
                    <p>{event.note || "No service note recorded."}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="armory-review-empty">No service actions have been recorded yet. When staff sends a firearm to maintenance, completes service, or records a return issue, it will appear here.</div>
            )}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
